import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Title } from "@/mocks/titles";

interface HeroCarouselProps {
  items: Title[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  return (
    <section className="relative w-full h-[560px] md:h-[720px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={item.still}
            alt={`《${item.name}》剧照`}
            title={`${item.name} 星光传媒推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/50"></div>

      <div className="relative z-10 h-full px-4 md:px-6 flex items-end pb-16 md:pb-24">
        <div className="max-w-[1440px] mx-auto w-full">
          <div className="max-w-2xl">
            <div key={current.id} className="slide-fade">
              <div className="flex flex-wrap items-center gap-2 mb-3 md:mb-4">
                <span className="px-2.5 py-1 rounded text-[11px] md:text-xs font-medium bg-accent-500 text-primary-950">
                  编辑推荐
                </span>
                <span className="px-2.5 py-1 rounded text-[11px] md:text-xs bg-background-50/10 text-background-100 backdrop-blur-sm">
                  {current.type === "movie" ? "电影" : "剧集"}
                </span>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] md:text-xs bg-background-50/10 text-background-100 backdrop-blur-sm">
                  <i className="ri-star-fill text-accent-400 text-[11px] w-3 h-3 flex items-center justify-center"></i>
                  {current.rating.toFixed(1)}
                </span>
                <span className="text-[11px] md:text-xs text-background-300">
                  {current.genres.join(" · ")} / {current.year}
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-6xl font-semibold text-background-50 leading-none mb-2 md:mb-3">
                {current.name}
              </h2>
              <p className="text-xs md:text-sm tracking-[0.2em] uppercase text-background-300/70 mb-4">{current.enName}</p>
              <p className="text-sm md:text-base text-background-200/90 leading-relaxed mb-6 line-clamp-2 md:line-clamp-3 max-w-xl">
                {current.synopsis}
              </p>
              <div className="flex items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-base w-4 h-4 flex items-center justify-center"></i>
                  查看详情
                </Link>
                <Link
                  to={`/category?type=${current.type}`}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-50/30 text-background-50 text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  更多{current.type === "movie" ? "电影" : "剧集"}
                  <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-10 flex items-end justify-between gap-4">
            <div className="flex items-center gap-2">
              {items.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到 ${item.name}`}
                  onClick={() => setIndex(i)}
                  className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                    i === index ? "w-10 bg-accent-400" : "w-5 bg-background-50/30 hover:bg-background-50/50"
                  }`}
                ></button>
              ))}
              <span className="ml-3 text-xs text-background-300 tabular-nums">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => setIndex((prev) => (prev - 1 + items.length) % items.length)}
                className="w-10 h-10 rounded-full border border-background-50/25 text-background-100 flex items-center justify-center hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => setIndex((prev) => (prev + 1) % items.length)}
                className="w-10 h-10 rounded-full border border-background-50/25 text-background-100 flex items-center justify-center hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <Link
        to={`/detail/${current.id}`}
        className="hidden lg:block absolute right-8 bottom-24 w-40 xl:w-48 rounded-lg overflow-hidden border border-background-50/15 cursor-pointer"
      >
        <img
          src={current.poster}
          alt={`《${current.name}》海报`}
          title={`${current.name} 星光传媒推荐`}
          className="w-full aspect-[3/4] object-cover object-top"
        />
      </Link>
    </section>
  );
}